/**
 * firmaProfileService.js — Firma Ana Sayfa (Dekoratör görünümü) için firma profili,
 * kurulum/söküm personel kadrosu ve firma bazlı fuar evrakları.
 *
 * standOverviewService.js'teki stand/proje evrakı verisinden farklı bir varlık —
 * orası standlara/projelere ait belgeleri tutar, burası firmanın KENDİSİNE ait
 * profil bilgisi + personel + fuar katılım evraklarını (Fuar Sözleşmesi,
 * Katılımcı El Kitabı, Karalaj Planı vb.) tutar. İkisi ayrı servis dosyaları
 * olarak kalır çünkü farklı varlıklardır.
 *
 * Base URL (future): https://gw.sakagrup.com:8443/api/v1/firma/{firmaId}/profile
 * Auth: getAuthHeader()
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { isgGrubu, grupBelgeKareleri, fuaraOzelBelgeler } from '../Components/Firma/belgeGruplari';
import i18n, { currentLanguageId } from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/firma`;

export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

// Test hesabı (testfirmamod, grup 9 - Firma Moderatör) için mock dekoratör firma kimliği.
// Gerçek login/user endpoint'i firmaId dönene kadar sayfa bu id'yi varsayılan kullanır.
export const MOCK_DECORATOR_FIRMA_ID = 500;

// 2026-08-10 reconciliation (bkz. docs/guides/FE-FirmaController-guide.md) — firma 49858
// "Örnek Yapı A.Ş." fuar 298 üzerinde backend'in doldurduğu tam mock fixture'ı taşıyor
// (profile, 5 belge her statüden birer örnek, 2 sağlık-güvenlik yetkilisi). forceLive:true
// geçen çağrılar bu id'yi kullanmalı — MOCK_DECORATOR_FIRMA_ID (500) gerçek backend'de yok.
// firmaTalepListService.js'teki aynı isimli sabitin dosya-yerel kopyası (bkz. o dosyadaki
// aynı desen, util/knowledgebase/local-backend-testing.md).
export const TEST_DECORATOR_FIRMA_ID = 49858;
export const TEST_FUAR_ID = 298;

// Gerçek backend file_type id'leri (bkz. rehberin Constants bölümü) — firma belgeleri
// parent_id=47 altında yaşıyor. KVKK bu makinede 86 (sabitin 81 drift uyarısı bu ortam
// için geçersiz — rehberde doğrulandı).
const BELGE_KEY_TO_FILE_TYPE_ID = {
  firmaIsgTaahhutnamesi: 79,
  isYeriHekimiAtamasi: 88,
  isGuvenligiUzmaniAtamasi: 89,
};
// Firma Belgeleri grubunda gösterilir ama firma panelinden YÜKLENMEZ (#156 kararı).
export const FUAR_SOZLESMESI_FILE_TYPE_ID = 80;

// documents/upload ve saglik-guvenlik-yetkilileri'nin uploadedBy/reviewedBy alanları
// session'dan gelmiyor (bkz. rehber "no endpoint here reads the session") — gerçek bir
// user_id şart, X-user-id:1 yetmiyor (user 1 yerelde yok). Rehberin kendi seed
// örnekleriyle aynı id'ler: 21360 (firma 48832 kullanıcısı), 9490 (panel kullanıcısı).
const TEST_UPLOADED_BY_USER_ID = 21360;

const _mockProfiles = {
  [MOCK_DECORATOR_FIRMA_ID]: {
    firmaId: MOCK_DECORATOR_FIRMA_ID,
    firmaName: 'TEST DECORATOR YAPI DEKORASYON LTD. ŞTİ.',
    firmaShortName: 'Test Decorator',
    firmaLogo: null,
    vergiLevhasiStatus: 'tamamlandi',   // 'tamamlandi' | 'eksik'
    imzaSirkusuStatus: 'tamamlandi',
    taahhutnameStatus: 'eksik',
    yetkiliDanisman: 'John Doe',
    adres: '123 Test Street, Test City, Test Country',
    telefon: '+90 555 555 5555',
    eposta: 'info@testdecorator.com',
    webSitesi: 'www.testdecorator.com',
    // Firma Kartı (Firma Bilgileri sekmesi) — katalog/sektör bilgisi
    kategoriler: ['İç Mimari ve Dekorasyon', 'Yapı ve İnşaat Malzemeleri', 'Fuar Stand Tasarımı', 'Mobilya ve Aksesuar'],
    katalogAciklama: 'Fuar ve etkinlik standları için iç mimari tasarım, üretim ve montaj hizmetleri sunuyoruz. Modüler ve özel tasarım çözümlerimizle katılımcı firmalara uçtan uca dekorasyon deneyimi sağlıyoruz.',
  },
};

// Firma Kartı — "Kullanıcılar": firmanın portalda hesabı olan tüm kullanıcıları
// (Ana Sayfa'daki "Kurulum/Söküm Kartı" personel kadrosundan farklı bir varlık).
const _mockKullanicilar = {
  [MOCK_DECORATOR_FIRMA_ID]: [
    { id: 1, adSoyad: 'Zeynep Arslan', email: 'zeynep.arslan@testdecorator.com', telefon: '+90 555 111 1111', unvan: 'Genel Müdür',    kayitTarihi: '2022-01-10', sonGiris: '2024-06-02', rol: 'Admin',     durum: 'aktif' },
    { id: 2, adSoyad: 'Kerem Doğan',   email: 'kerem.dogan@testdecorator.com',   telefon: '+90 555 222 2222', unvan: 'Proje Sorumlusu', kayitTarihi: '2022-05-14', sonGiris: '2024-05-20', rol: 'Kullanıcı', durum: 'aktif' },
    { id: 3, adSoyad: 'Elif Vergili',  email: 'elif.vergili@testdecorator.com',  telefon: '+90 555 333 3333', unvan: 'Muhasebe',        kayitTarihi: '2022-08-01', sonGiris: '2023-11-02', rol: 'Kullanıcı', durum: 'pasif' },
  ],
};

// Firma Kartı — "Firma Yetkilileri": firmanın atadığı İSG yetkilileri VE firma
// yetkilisi (eski adıyla firma temsilcisi, #105). Evraklar sekmesindeki atama belgesi durumundan (isYeriHekimiAtamasi/
// isGuvenligiUzmaniAtamasi, bkz. _mockFirmaBelgeleri) ayrı bir kayıt; burası kişileri
// tutar. Backlog #63: satırlar artık #62'nin rol katalogundaki alanları da taşıyor
// (rolAd/grupKod/grupAd/sira) — gruplama bunlarla yapılıyor, bkz.
// Components/Firma/yetkiliGruplari.js. grupSira BİLEREK yok: gerçek backend de satırda
// göndermiyor, grup sırası _mockYetkiliRolleri'nden eşleşiyor.
const _mockSaglikGuvenlikYetkilileri = {
  [MOCK_DECORATOR_FIRMA_ID]: [
    { id: 1, rol: 'isYeriHekimi', rolAd: 'İş Yeri Hekimi', grupKod: 'isg', grupAd: 'İSG Yetkilileri', sira: 1, unvan: 'İş Yeri Hekimi', adSoyad: 'Dr. Murat Şahin', telefon: '+90 555 444 4444', email: 'murat.sahin@testdecorator.com', fuarId: null, atamaBelgesiStatus: 1, atamaBelgesiFileTypeId: 88 },
    { id: 2, rol: 'isGuvenligiUzmani', rolAd: 'İş Güvenliği Uzmanı', grupKod: 'isg', grupAd: 'İSG Yetkilileri', sira: 2, unvan: 'A Sınıfı İş Güvenliği Uzmanı', adSoyad: 'Selin Kaya', telefon: '+90 555 666 6666', email: 'selin.kaya@testdecorator.com', fuarId: null, atamaBelgesiStatus: 0, atamaBelgesiFileTypeId: 89 },
    // #105/#106: profil alanları + bağlı hesap (kullanıcı 1, bkz. _mockKullanicilar). Fotoğraf
    // BİLEREK null — demo görünümünde ikon fallback'i görünsün; yükleme akışı mock'ta çalışıyor.
    {
      id: 3, rol: 'firmaTemsilcisi', rolAd: 'Firma Yetkilisi', grupKod: 'temsilci', grupAd: 'Firma Yetkilisi', sira: 1,
      unvan: 'Genel Müdür', adSoyad: 'Zeynep Arslan', telefon: '+90 555 111 1111', email: 'zeynep.arslan@testdecorator.com',
      fuarId: null, atamaBelgesiStatus: null, atamaBelgesiFileTypeId: null,
      departman: 'Yönetim', dogumTarihi: '1980-01-01', linkedin: 'linkedin.com/in/zeynep-arslan',
      biyografi: 'Zeynep Arslan, 2008 yılından beri fuar standı ve dekorasyon projelerinde yönetici olarak çalışıyor. Firmanın fuar operasyonlarından ve Saka Grup ile olan tüm yazışmalardan sorumlu.',
      fotografUrl: null,
      kullaniciId: 1, kullaniciAdi: 'zeynep.arslan', kullaniciRol: 'Admin', kullaniciAktif: true,
      kullaniciKayitTarihi: '2022-01-10T09:30:00', kullaniciSonGiris: '2024-06-02T14:05:00',
    },
  ],
};

// #62'nin `firma_yetkili_rol` tablosunun seed'iyle birebir aynı — demo görünümünde de
// gruplar/başlıklar katalogdan türesin diye. Gerçek katalog yalnızca aktif rolleri döner.
const _mockYetkiliRolleri = [
  { id: 1, kod: 'isYeriHekimi',      ad: 'İş Yeri Hekimi',      grupKod: 'isg',      grupAd: 'İSG Yetkilileri',  grupSira: 1, sira: 1, atamaFileTypeId: 88,   unvanIpucu: 'İş Yeri Hekimi',                tekil: false },
  { id: 2, kod: 'isGuvenligiUzmani', ad: 'İş Güvenliği Uzmanı', grupKod: 'isg',      grupAd: 'İSG Yetkilileri',  grupSira: 1, sira: 2, atamaFileTypeId: 89,   unvanIpucu: 'A Sınıfı İş Güvenliği Uzmanı',  tekil: false },
  { id: 3, kod: 'firmaTemsilcisi',   ad: 'Firma Yetkilisi',     grupKod: 'temsilci', grupAd: 'Firma Yetkilisi',   grupSira: 2, sira: 1, atamaFileTypeId: null, unvanIpucu: 'Müdür, Direktör…',              tekil: true },
];

// Firma Kartı — "Görüşme Notları": Saka Grup ekibinin bu firma ile yaptığı görüşme kayıtları.
const _mockGorusmeNotlari = {
  [MOCK_DECORATOR_FIRMA_ID]: [
    { id: 1, tarih: '2024-05-02', saat: '14:00', gorusulen: 'Zeynep Arslan', denetmen: 'Ali Veli',   gorusmeTipi: 'Telefon',   not: 'Fuar öncesi ön görüşme yapıldı.' },
    { id: 2, tarih: '2024-05-10', saat: '11:30', gorusulen: 'Kerem Doğan',   denetmen: 'Ayşe Demir', gorusmeTipi: 'Yüz yüze', not: 'Stand kroki teslimi görüşüldü.' },
    { id: 3, tarih: '2024-06-01', saat: '09:15', gorusulen: 'Zeynep Arslan', denetmen: 'Ali Veli',   gorusmeTipi: 'Mail',      not: 'Sözleşme detayları paylaşıldı.' },
  ],
};

// Kurulum/Söküm kartı alabilecek personel kadrosu — Ana Sayfa'daki "Kurulum ve
// Söküm Kartı Durumu" panelinin ve Personel Listesi'nin (firmaPersonelListService.js,
// /Firma/Personel) ortak tek kaynağı. documents: KVKK onayı + personel başına
// SGK/İSG/Sağlık/Mesleki Eğitim belge durumları (FILE_APPROVAL_STATUS kodlarıyla
// aynı: 0=Beklemede, 1=Onaylandı, 2=Reddedildi, null=Kayıt yok). uyruk: serbest
// metin; role: 'isveren' | 'isci' — firma başına genelde tek bir işveren olur.
const _mockPersonnel = {
  [MOCK_DECORATOR_FIRMA_ID]: [
    {
      id: 1, name: 'Eyüp Gürbüz', phone: '+90 555 111 22 33', tcNo: '11223344558', photo: null,
      uyruk: 'Türkiye', role: 'isveren',
      documents: {
        kvkk: { status: 0 }, sgk: { status: null }, isg: { status: 0 },
        saglik: { status: 0 }, meslekiEgitim: { status: null },
      },
    },
    {
      id: 2, name: 'Efe Kaya', phone: '+90 555 111 22 44', tcNo: '22334455661', photo: null,
      uyruk: 'Türkiye', role: 'isci',
      documents: {
        kvkk: { status: 1 }, sgk: { status: 1 }, isg: { status: 1 },
        saglik: { status: 1 }, meslekiEgitim: { status: 1 },
      },
    },
    {
      id: 3, name: 'Elif Vergili', phone: '+90 555 111 22 55', tcNo: '33445566774', photo: null,
      uyruk: 'Azerbaycan', role: 'isci',
      documents: {
        kvkk: { status: 2 }, sgk: { status: 0 }, isg: { status: null },
        saglik: { status: 0 }, meslekiEgitim: { status: null },
      },
    },
    {
      id: 4, name: 'Mert Aydın', phone: '+90 555 111 22 66', tcNo: '44556677887', photo: null,
      uyruk: 'Türkiye', role: 'isci',
      documents: {
        kvkk: { status: 1 }, sgk: { status: null }, isg: { status: null },
        saglik: { status: null }, meslekiEgitim: { status: null },
      },
    },
  ],
};

// Firma çapında, personel başına değil tek kayıt: Firma İSG Taahhütnamesi / İş
// Yeri Hekimi Ataması / İş Güvenliği Uzmanı Ataması — firma tarafından yüklenir.
// Üçü de onaylıysa personelin bireysel SGK/İSG/Sağlık/Mesleki Eğitim belgelerine
// gerek kalmaz (bkz. firmaBelgeleriAggregateStatus / computeCardEligible altta).
const _mockFirmaBelgeleri = {
  [MOCK_DECORATOR_FIRMA_ID]: {
    firmaIsgTaahhutnamesi: { status: 1 },
    isYeriHekimiAtamasi: { status: 0 },
    isGuvenligiUzmaniAtamasi: { status: null },
  },
};

// Backlog #156 (backend #123) — firma belgeleri İKİ grupta: Firma Belgeleri (Vergi Levhası/
// İmza Sirküsü/Faaliyet Belgesi grubu belirler; Fuar Sözleşmesi seçili fuar için yanında
// gösterilir ama SAYILMAZ, zorunlu:false) ve Firma İSG Belgeleri (yukarıdaki üçlü). Canlıda
// gruplar profile-page'in belgeGruplari[]'ından gelir; bu katalog backend'deki
// firma_belge_grup satırlarının yalnızca mock görünümü için kopyası.
const _MOCK_BELGE_GRUP_KATALOG = [
  {
    kod: 'FIRMA', ad: 'Firma Belgeleri', belgeler: [
      { fileTypeId: 77, ad: 'Vergi Levhası', zorunlu: true, fuaraOzel: false },
      { fileTypeId: 78, ad: 'İmza Sirküsü', zorunlu: true, fuaraOzel: false },
      { fileTypeId: 94, ad: 'Faaliyet Belgesi', zorunlu: true, fuaraOzel: false },
      { fileTypeId: 80, ad: 'Fuar Sözleşmesi', zorunlu: false, fuaraOzel: true },
    ],
  },
  {
    kod: 'ISG', ad: 'Firma İSG Belgeleri', belgeler: [
      { fileTypeId: 79, ad: 'Firma İSG Taahhütnamesi', zorunlu: true, fuaraOzel: false },
      { fileTypeId: 88, ad: 'İş Yeri Hekimi Ataması', zorunlu: true, fuaraOzel: false },
      { fileTypeId: 89, ad: 'İş Güvenliği Uzmanı Ataması', zorunlu: true, fuaraOzel: false },
    ],
  },
];

// İSG dışındaki grup belgelerinin mock durumu (fileTypeId → contract status). İSG üçlüsü
// _mockFirmaBelgeleri'nden okunur ki Personel sayfalarının mock muafiyetiyle aynı kalsın.
const _mockFirmaGrupBelgeDurumlari = {
  [MOCK_DECORATOR_FIRMA_ID]: { 77: 1, 78: 1, 94: null, 80: 1 },
};

const ISG_BELGE_KEY_BY_FILE_TYPE_ID = Object.fromEntries(
  Object.entries(BELGE_KEY_TO_FILE_TYPE_ID).map(([key, id]) => [id, key]),
);

function mockBelgeGruplari(firmaId) {
  const firmaBelgeleri = _mockFirmaBelgeleri[firmaId] || {};
  const digerleri = _mockFirmaGrupBelgeDurumlari[firmaId] || {};
  return _MOCK_BELGE_GRUP_KATALOG.map((grup) => {
    const belgeler = grup.belgeler.map((b) => {
      const isgKey = ISG_BELGE_KEY_BY_FILE_TYPE_ID[b.fileTypeId];
      const status = isgKey ? firmaBelgeleri[isgKey]?.status : digerleri[b.fileTypeId];
      return { ...b, status: status ?? null };
    });
    // Backend'deki kuralın aynısı, yalnızca zorunlu belgeler üzerinden.
    const status = firmaBelgeleriAggregateStatus(belgeler.filter((b) => b.zorunlu));
    return { kod: grup.kod, ad: grup.ad, status, tamam: status === 1, belgeler };
  });
}

// KVKK gereği TC Kimlik No'yu maskeler — ilk 3 ve son 2 hane görünür, kalanı yıldız.
export function maskTcNo(tcNo) {
  if (!tcNo || tcNo.length < 6) return tcNo || '—';
  return `${tcNo.slice(0, 3)}${'*'.repeat(tcNo.length - 5)}${tcNo.slice(-2)}`;
}

// Firma çapındaki 3 belgenin birleşik durumu: biri reddedildiyse reddedildi,
// üçü de onaylıysa onaylandı (bu durumda personelin bireysel belgelerine muafiyet
// uygulanır — Personel Listesi kartındaki "Firma İSG Belgeleri" ikonu), en az biri
// başladıysa beklemede, hiçbiri başlamadıysa kayıt yok.
export function firmaBelgeleriAggregateStatus(firmaBelgeleri) {
  const statuses = Object.values(firmaBelgeleri || {}).map((d) => d.status);
  if (statuses.some((s) => s === 2)) return 2;
  if (statuses.length > 0 && statuses.every((s) => s === 1)) return 1;
  if (statuses.some((s) => s === 0 || s === 1)) return 0;
  return null;
}

const PERSONEL_BELGE_KEYS = ['sgk', 'isg', 'saglik', 'meslekiEgitim'];

// Kurulum/Söküm kartı alabilmesi için: KVKK onaylı olmalı ve (bireysel 4 belgesi
// tamam olmalı YA DA firma belgeleri muafiyet sağlamalı).
export function computeCardEligible(person, firmaBelgeleri) {
  const muaf = firmaBelgeleriAggregateStatus(firmaBelgeleri) === 1;
  const kvkkOk = person.documents.kvkk.status === 1;
  const personelTamam = muaf || PERSONEL_BELGE_KEYS.every((k) => person.documents[k].status === 1);
  return kvkkOk && personelTamam;
}

const _mockDocuments = {
  [MOCK_DECORATOR_FIRMA_ID]: [
    { key: 'fuarSozlesmesi',    label: 'Fuar Sözleşmesi',      status: 'onayli' },  // onayli | eksik | yuklendi
    { key: 'katilimciElKitabi', label: 'Katılımcı El Kitabı',  status: 'eksik' },
    { key: 'karalajPlani',      label: 'Karalaj Planı',        status: 'yuklendi' },
  ],
};

// Ana Sayfa — "İletişim" widget'ı: dekoratörün ulaşabileceği Saka Grup (organizatör)
// tarafı kişileri — Firma Kartı'ndaki firmanın KENDİ yetkilileri/kullanıcılarından
// (getFirmaKullanicilar/getYetkiliRolleri) TAMAMEN AYRI bir varlık.
//
// WAS (until #538): `GET /firma/{firmaId}/iletisim-kisileri` backend'de yoktu, bu yüzden widget bu dosyadaki iki sabit
// satıra düşüyordu (_mockIletisimKisileri: Atanmış Danışman 'John Doe' + Teknik Destek Hattı; _genericIletisimKisileri:
// yalnızca Teknik Destek Hattı, +90 850 200 10 00 / teknikdestek@sakagrup.com). Backend #537 uç ve tabloyu getirdi
// (personel Sistem Ayarları › İletişim Kişileri'nden yönetiyor); getFirmaIletisimKisileri artık hep canlı.

/**
 * GET /firma/{firmaId}/profile
 * forceLive: GET /firma/{firmaId}/profile-page üzerinden okur (tasks-3 için özel
 * kurgulanmış composed read — eski /profile ham kaydı değil, bkz. rehber "Profile page"
 * bölümü). `profile` alt-nesnesi mock'un düz şekliyle zaten eşleşiyor (firmaShortName,
 * *Status alanları vb.), sadece kvkkStatus fazladan geliyor (yok sayılır).
 *
 * Backlog #60: dönen nesneye `fotograflar[]` de iliştiriliyor. Galeri cevabın ÜST
 * seviyesinde (`profile`in içinde değil) geldiği için burada birleştiriliyor — böylece
 * Firma Kartı sayfası fotoğraflar için AYRI bir istek atmıyor. `profile` ayrıca
 * latitude/longitude, kategoriDetay[] ve firmaLogoUrl/firmaLogoSource taşır (#61).
 *
 * Backlog #156: aynı şekilde `belgeGruplari[]` de iliştiriliyor — [{ kod: 'FIRMA'|'ISG', ad,
 * status, tamam, belgeler: [{ fileTypeId, ad, zorunlu, fuaraOzel, status }] }], status'ler
 * contract numaralamasıyla (FILE_APPROVAL_STATUS). Grubu HER ZAMAN `kod` ile bul, `ad` ile
 * değil. `fuarId` yalnızca fuara özel belgelerin (Fuar Sözleşmesi) durumunu belirler;
 * verilmezse o belgenin status'ü hep null döner.
 */
export async function getFirmaProfile(firmaId, { forceLive = false, fuarId } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const profile = _mockProfiles[firmaId];
    if (!profile) throw new Error('Firma profili bulunamadı');
    // Mock fixture'da fotoğraf/koordinat yok — canlı şekille aynı anahtarlar dönsün ki
    // sayfa iki ayrı şekle bakmak zorunda kalmasın.
    return { ...profile, fotograflar: [], belgeGruplari: mockBelgeGruplari(firmaId) };
  }

  try {
    const response = await axios.get(`${BASE}/${firmaId}/profile-page`, {
      params: fuarId ? { fuarId } : undefined,
      headers: getAuthHeader(),
    });
    return {
      ...response.data.profile,
      fotograflar: response.data.fotograflar || [],
      belgeGruplari: response.data.belgeGruplari || [],
    };
  } catch (error) {
    console.error('Error fetching firma profile:', error);
    throw error;
  }
}

/**
 * Kurulum/söküm kartı alabilecek personel kadrosu.
 * GET /firma/{firmaId}/personnel
 */
export async function getFirmaPersonnel(firmaId) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const firmaBelgeleri = _mockFirmaBelgeleri[firmaId] || {};
    return (_mockPersonnel[firmaId] || []).map((p) => ({
      ...p,
      maskedTcNo: maskTcNo(p.tcNo),
      cardEligible: computeCardEligible(p, firmaBelgeleri),
    }));
  }

  try {
    const response = await axios.get(`${BASE}/${firmaId}/personnel`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching firma personnel:', error);
    throw error;
  }
}

/**
 * Firma çapında Kurulum/Söküm için gereken 3 belge (Firma İSG Taahhütnamesi,
 * İş Yeri Hekimi Ataması, İş Güvenliği Uzmanı Ataması) — personel başına değil
 * firma başına tek kayıt.
 * GET /firma/{firmaId}/belgeler
 */
export async function getFirmaBelgeleri(firmaId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return { ...(_mockFirmaBelgeleri[firmaId] || {}) };
  }

  try {
    // Ayrı bir /belgeler rotası yok — backend bunu /profile-page'in `belgeler` alanına
    // gömdü (contract numaralaması: null/0 Beklemede/1 Onaylandı/2 Reddedildi — FILE_APPROVAL_STATUS
    // ile aynı sözlük, bkz. FirmaKartPage.jsx firmaBelgeStatusInfo). Mock'un
    // {belgeKey: {status}} şeklini korumak için burada çeviriyoruz ki sayfa değişmesin.
    const response = await axios.get(`${BASE}/${firmaId}/profile-page`, { headers: getAuthHeader() });
    return belgeleriFromProfilePage(response.data);
  } catch (error) {
    console.error('Error fetching firma belgeleri:', error);
    throw error;
  }
}

// #258 — ISG grubu varsa belgeleri ondan kur (İşveren görevlendirmesi 99 yalnızca grup taşıyorsa çıkar; işveren yolunda
// onaysız 88/89 `ok` sayılır). Anahtar: bilinen üçlüde eski ad, diğerinde `belge{fileTypeId}`; tüketiciler
// `ok ?? status === 1` ve `ad ?? etiket` okur (mock şekli aynen). #388: getFirmaBelgeleri ve getFirmaEvrakOzeti ortak kullanır.
function belgeleriFromProfilePage(data) {
  const isg = isgGrubu(data.belgeGruplari);
  if (isg) {
    const anahtarlar = Object.fromEntries(Object.entries(BELGE_KEY_TO_FILE_TYPE_ID).map(([k, id]) => [id, k]));
    return Object.fromEntries(
      grupBelgeKareleri(isg).map((k) => [
        anahtarlar[k.fileTypeId] ?? `belge${k.fileTypeId}`,
        { status: k.status, ad: k.ad, ok: k.ok },
      ]),
    );
  }
  const b = data.belgeler || {};
  return {
    firmaIsgTaahhutnamesi: { status: b.firmaIsgTaahhutnamesiStatus ?? null },
    isYeriHekimiAtamasi: { status: b.isYeriHekimiAtamasiStatus ?? null },
    isGuvenligiUzmaniAtamasi: { status: b.isGuvenligiUzmaniAtamasiStatus ?? null },
  };
}

/**
 * Backlog #388 (backend #186) — bir firmanın evrak ÖZETİ, Stand Durumu'nun "Firma İSG Evrakları" kutusu için:
 * { profile, documents, firmaBelgeleri, kitle } = standDurumuService.evrakDurumuOk'un okuduğu üç parça + okuyucunun kitlesi.
 *
 * Canlı: TEK `GET /firma/{id}/profile-page?fuarId=` (eskiden üç okuma: profile-page, GET /documents, bir daha profile-page).
 * `GET /documents` yalnızca kendi firması ya da personel içindir (#84), bu yüzden bir Katılımcı, standındaki dekoratörün
 * evrakını hep "Veri Yok" görüyordu; profile-page ise ortak standı olan firmaya (kitle ORTAK, `?fuarId=` şart) belge
 * durumlarını ve `belgeGruplari`nı verir (dosya yok). `documents` grup cevabının fuaraOzel satırlarından kurulur.
 * `kitle`: PERSONEL / KENDI / ORTAK belge durumunu görür; HERKES göremez (belgeler null, belgeGruplari []): "Eksik" demek
 * yerine `profile: null` döner, yani çağıran "Veri Yok" gösterir.
 * Mock: eski üç okuma, `kitle: null`.
 */
export async function getFirmaEvrakOzeti(firmaId, { forceLive = false, fuarId } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    const [profile, documents, firmaBelgeleri] = await Promise.all([
      getFirmaProfile(firmaId),
      getFirmaDocuments(firmaId, { fuarId }),
      getFirmaBelgeleri(firmaId),
    ]);
    return { profile, documents, firmaBelgeleri, kitle: null };
  }

  try {
    const response = await axios.get(`${BASE}/${firmaId}/profile-page`, {
      params: fuarId ? { fuarId } : undefined,
      headers: getAuthHeader(),
    });
    const data = response.data;
    const kitle = data.kitle ?? null;
    if (kitle === 'HERKES') return { profile: null, documents: [], firmaBelgeleri: {}, kitle };
    return {
      profile: { ...data.profile, belgeGruplari: data.belgeGruplari || [] },
      documents: fuaraOzelBelgeler(data.belgeGruplari),
      firmaBelgeleri: belgeleriFromProfilePage(data),
      kitle,
    };
  } catch (error) {
    console.error('Error fetching firma evrak ozeti:', error);
    throw error;
  }
}

// Kişi belgesi yükleme (sgk/isg/saglik/meslekiEgitim) — gerçek `file_type_id`ler
// (bkz. docs/guides/FE-PersonnelController-guide.md Constants bölümü, KisiDocumentConstant).
// kvkk bu haritada yok, kendi ayrı ucu var (bkz. altındaki forceLive dalı).
// Backlog #142: SGK/İSG artık iki belge — burada TEK KİŞİLİK varyantlar (95 İşe Giriş Bildirgesi,
// 97 İSG Sertifikası); eski 6/7 yeni yüklemelerde sunulmuyor. Paylaşımlı varyantlar (96/98) bu
// uçtan YÜKLENMEZ (400), personelBelgeService.js'in paylaşımlı uçlarına gider.
const PERSONEL_BELGE_KEY_TO_FILE_TYPE_ID = { sgk: 95, isg: 97, saglik: 8, meslekiEgitim: 9 };

/**
 * Personelin bireysel bir belgesini (KVKK/SGK/İSG/Sağlık/Mesleki Eğitim) yükler
 * — mock modda durum "Beklemede"ye (0) döner, admin onayı ayrı bir akıştır (bkz.
 * aynı desen uploadStandFile — standOverviewService.js).
 * Mock: POST /firma/{firmaId}/personnel/{personelId}/belgeler/{belgeKey} (multipart: file)
 *
 * 2026-08-11 revizyonu (mock→backend reconciliation): canlı dalda diğer dördü
 * `POST /personnel/dosyaekle/{personId}` (multipart: file_type_id, file). ⚠️ Gerçek backend'de
 * yeni yükleme "Beklemede"ye DÖNMEZ — dosya süresi dolana kadar (dosyaekle varsayılan +60 gün —
 * bkz. rehberin trap #21) review kaydı olmadan doğrudan "Onaylandı" (1) okunur; mock'un "onay
 * bekleniyor" akışı canlıda gerçekleşmiyor, çağıran sayfa (FirmaPersonelDetailPage.jsx) bunu bilerek
 * farklı bir bilgi mesajıyla gösteriyor. `fuarId` belgenin fuarıdır (`file.fuar_id`): #387'den beri form alanı `fuar_id`
 * olarak gider; verilmezse backend kullanıcının SEÇİLİ fuarını veritabanından okur. Firma girişi yalnızca firmasının
 * fuarlarını gönderebilir (403).
 * WAS (until #387): "`fuarId` dosyaekle'de gerekli (`file.fuar_id`, bkz. rehberin "X-fuar-id" notu)" — başlığı backend
 * okumuyordu, fuar oturumdan (GİRİŞ anındaki fuar) alınıyordu.
 *
 * Backlog #252 (2026-10-02): KVKK artık BURADAN yüklenmez. KVKK kişinin kendi rızasıdır; backend #249'dan
 * beri firma hesabı hem `/kartduzenle/{id}/kvkk` hem `/dosyaekle` file_type 56 için 403 alır (belge
 * 2030-01-01 son kullanma ile yazılır — eskiden "BUGÜN" idi, onaylanmamış KVKK iki günde düşüyordu).
 * Denetmen yüklemesi: personelBelgeService.uploadKvkkBelgesi.
 *
 * #372 (backend #371): `meslekId` yalnızca `meslekiEgitim`de anlamlı — sertifikayı kişinin o fuardaki mesleklerinden
 * birine etiketler (firma, başka firmanın verdiği mesleği de etiketleyebilir). Etiketsiz sertifika 2+ meslek varken
 * hiçbirine sayılmaz.
 */
export async function uploadPersonelBelgesi(firmaId, personelId, belgeKey, file, { forceLive = false, fuarId, meslekId } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const person = (_mockPersonnel[firmaId] || []).find((p) => String(p.id) === String(personelId));
    if (!person) throw new Error('Personel bulunamadı');
    if (!person.documents[belgeKey]) throw new Error('Belge türü bulunamadı');
    person.documents[belgeKey] = { status: 0 };
    return { success: true };
  }

  try {
    const fileTypeId = PERSONEL_BELGE_KEY_TO_FILE_TYPE_ID[belgeKey];
    if (!fileTypeId) throw new Error(i18n.t("services:firmaProfileService.belgeTuruBulunamadi"));
    const formData = new FormData();
    formData.append('file_type_id', String(fileTypeId));
    formData.append('file', file);
    if (belgeKey === 'meslekiEgitim' && meslekId) formData.append('meslek_id', String(meslekId));
    if (Number(fuarId) > 0) formData.append('fuar_id', String(fuarId));   // #387
    const response = await axios.post(
      `${JAVA_BACKEND_API_URL}/personnel/dosyaekle/${personelId}`,
      formData,
      // WAS (until #387): { headers: { ...getAuthHeader(), 'X-fuar-id': fuarId, 'Content-Type': 'multipart/form-data' } },
      { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading personel belgesi:', error);
    throw error;
  }
}

/**
 * Backlog #156 — iki firma belge grubundan (getFirmaProfile'ın `belgeGruplari[]`ı) bir belgeyi
 * yükler; `belge` o grubun kendi satırıdır ({ fileTypeId, fuaraOzel }). Personel başına değil
 * firma başına tek kayıt, durum "Beklemede"ye (0) döner. İSG üçlüsü de buradan yüklenir (eski
 * belgeKey tabanlı uploadFirmaBelgesi'nin yerini aldı).
 * POST /firma/{firmaId}/documents/upload (multipart: file)
 *
 * ⚠️ fuarId YALNIZCA `fuaraOzel` belgede gönderilir. Backend fuarId'yi belge türüne göre
 * denetlemiyor: firma-geneli bir belge fuarId ile yüklenirse fuar_id dolu bir satır oluşur ve
 * firma-geneli okumaların hiçbiri onu görmez (yüklendiği halde grup "eksik" kalır).
 */
export async function uploadFirmaGrupBelgesi(firmaId, belge, file, { forceLive = false, fuarId } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const isgKey = ISG_BELGE_KEY_BY_FILE_TYPE_ID[belge.fileTypeId];
    const kayitlar = isgKey ? _mockFirmaBelgeleri[firmaId] : _mockFirmaGrupBelgeDurumlari[firmaId];
    const anahtar = isgKey || belge.fileTypeId;
    if (!kayitlar || !(anahtar in kayitlar)) throw new Error('Belge türü bulunamadı');
    kayitlar[anahtar] = isgKey ? { status: 0 } : 0;
    return { success: true };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);
    // fileTypeId/uploadedBy @RequestParam'dır (JSON gövde değil, bkz. rehber) — query
    // string'e gidiyorlar.
    const params = { fileTypeId: belge.fileTypeId, uploadedBy: TEST_UPLOADED_BY_USER_ID };
    if (belge.fuaraOzel) {
      if (!fuarId) throw new Error(i18n.t("services:firmaProfileService.buBelgeIcinOnceBir"));
      params.fuarId = fuarId;
    }
    const response = await axios.post(
      `${BASE}/${firmaId}/documents/upload`,
      formData,
      { params, headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading firma belgesi:', error);
    throw error;
  }
}

/**
 * Firmanın fuar katılım evrakları (Fuar Sözleşmesi, Katılımcı El Kitabı, Karalaj Planı).
 * GET /firma/{firmaId}/documents
 */
export async function getFirmaDocuments(firmaId, { forceLive = false, fuarId = TEST_FUAR_ID } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return (_mockDocuments[firmaId] || []).map((d) => ({ ...d }));
  }

  try {
    // Sadece fuarSozlesmesi (file_type 80) gerçek — katilimciElKitabi/karalajPlani mock'tu,
    // gerçek 7 firma belge türünün arasında yok, kasıtlı olarak düşürüldü (bkz.
    // rehberin "Asked for but not built" tablosu). Ham approvalStatus (0/1/2/3) mock'un
    // onayli|eksik|yuklendi sözlüğüne çevriliyor.
    const response = await axios.get(`${BASE}/${firmaId}/documents`, {
      params: { fuarId },
      headers: getAuthHeader(),
    });
    const fuarSozlesmesi = (response.data || []).find((d) => d.fileTypeId === FUAR_SOZLESMESI_FILE_TYPE_ID);
    if (!fuarSozlesmesi) return [];
    const status = fuarSozlesmesi.approvalStatus === 2 ? 'onayli'
      : fuarSozlesmesi.approvalStatus === 1 ? 'yuklendi'
      : 'eksik';
    return [{ key: 'fuarSozlesmesi', label: i18n.t("services:firmaProfileService.fuarSozlesmesi"), status }];
  } catch (error) {
    console.error('Error fetching firma documents:', error);
    throw error;
  }
}

/**
 * Backlog #282 — firmanın belge SATIRLARI, ham haliyle: Evraklar sekmesinde kutucuğun red nedenini
 * (`rejectReason`) ve gerçek dosyasını (`filePath`) göstermek için. Profile-page'in `belgeGruplari`
 * satırları ikisini de taşımıyor. Satırı kutucuğa `grupBelgesininSatiri` eşler.
 * GET /firma/{firmaId}/documents?fuarId= — firma kendi satırlarını okuyabilir.
 *
 * ⚠️ Ham `approvalStatus` 0 Yüklenmedi / 1 Beklemede / 2 Onaylandı / 3 Reddedildi'dir; profile-page'in
 * kutucuk durumu ise 0 Beklemede / 1 Onaylandı / 2 Reddedildi. Karışmasın diye çağıran kutucuğun durumuna
 * bakar, satırdan yalnızca nedeni/dosyayı/tarihi okur. Mock modu yok: demo görünümünde boş döner.
 * #212: Firma Kartı artık GET /evraklar'ı okuyor (firmaService.getFirmaEvraklari); çağıranı kalmadı.
 */
export async function getFirmaDocumentRows(firmaId, { forceLive = false, fuarId = TEST_FUAR_ID } = {}) {
  if (USE_MOCK_DATA && !forceLive) return [];

  try {
    const response = await axios.get(`${BASE}/${firmaId}/documents`, {
      params: { fuarId },
      headers: getAuthHeader(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching firma document rows:', error);
    throw error;
  }
}

/**
 * Ana Sayfa — "İletişim" widget'ı: dekoratörün ulaşabileceği Saka Grup tarafı
 * kişileri (atanmış danışman + teknik destek hattı gibi). Firma Kartı'ndaki
 * firmanın kendi yetkilileri/kullanıcılarıyla (getFirmaSaglikGuvenlikYetkilileri/getFirmaKullanicilar)
 * karıştırılmamalı — bkz. dosya başı İletişim yorumu.
 * GET /firma/{firmaId}/iletisim-kisileri (backend #537) — düz dizi [{ id, role, adSoyad, telefon, eposta, kapsam, fuarId, firmaId }],
 * istek dilinde (Accept-Language / languageId) ve BİRLEŞİK: önce firmanın kendi satırları, sonra fuarınkiler, sonra genel olanlar,
 * yalnızca görünür olanlar. telefon ya da eposta boş olabilir (en az biri dolu). fuarId yoksa girişin seçili fuarı kullanılır.
 * Kendi firması ya da personel; başkasının firması 403.
 * WAS (until #538): USE_MOCK_DATA dalı sabit bir Teknik Destek Hattı satırı dönüyordu; kaldırıldı.
 */
export async function getFirmaIletisimKisileri(firmaId, { fuarId, languageId = currentLanguageId() } = {}) {
  try {
    const params = { languageId };
    if (fuarId) params.fuarId = fuarId;
    const response = await axios.get(`${BASE}/${firmaId}/iletisim-kisileri`, { params, headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching firma iletisim kisileri:', error);
    throw error;
  }
}

/**
 * Firma Kartı — firmanın portalda hesabı olan tüm kullanıcıları.
 * GET /firma/{firmaId}/kullanicilar
 */
export async function getFirmaKullanicilar(firmaId, { forceLive = false, fuarId = TEST_FUAR_ID } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return (_mockKullanicilar[firmaId] || []).map((k) => ({ ...k }));
  }

  try {
    // Ayrı bir /kullanicilar rotası yok — aynı /detay → users kaynağı, dedupe aynı
    // desen. kayitTarihi/sonGiris/durum backend'de yok; durum
    // hesap var olduğu için 'aktif' varsayılıyor (rehber: "Missing vs. the contract:
    // kayitTarihi, sonGiris, durum").
    const response = await axios.get(`${BASE}/${firmaId}/detay`, {
      params: { fuarId },
      headers: getAuthHeader(),
    });
    const seen = new Set();
    const users = (response.data.users || []).filter((u) => {
      if (seen.has(u.userId)) return false;
      seen.add(u.userId);
      return true;
    });
    return users.map((u) => ({
      id: u.userId,
      adSoyad: `${u.userName || ''} ${u.userSurname || ''}`.trim(),
      email: u.userEmail,
      telefon: u.userPhone,
      unvan: null,
      kayitTarihi: null,
      sonGiris: null,
      rol: u.userGroupName,
      durum: 'aktif',
    }));
  } catch (error) {
    console.error('Error fetching firma kullanicilar:', error);
    throw error;
  }
}

/**
 * Firma yetkilisi rol KATALOĞU — grupların adı/sırası ve ekleme formunun seçenekleri
 * buradan türer (bkz. Components/Firma/yetkiliGruplari.js). Bu sayfa `profile-page`i
 * çekmediği için katalog AYRI bir çağrı: yetkili satırları `grupSira` taşımıyor, yani
 * katalog olmadan gruplar doğru sıralanamaz ve boş bir grup (henüz atanmamış Firma
 * Yetkilisi) hiç görünmezdi.
 * GET /firma/yetkili-rolleri
 */
export async function getYetkiliRolleri({ forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockYetkiliRolleri.map((r) => ({ ...r }));
  }

  try {
    const response = await axios.get(`${BASE}/yetkili-rolleri`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching yetkili rolleri:', error);
    throw error;
  }
}

/**
 * Firma Kartı — firma tarafından atanmış İş Yeri Hekimi ve İş Güvenliği Uzmanı'nın
 * kişi/iletişim bilgileri (Evraklar sekmesindeki atama belgesi durumundan ayrı).
 * GET /firma/{firmaId}/saglik-guvenlik-yetkilileri
 */
export async function getFirmaSaglikGuvenlikYetkilileri(firmaId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return (_mockSaglikGuvenlikYetkilileri[firmaId] || []).map((k) => ({ ...k }));
  }

  try {
    // Gerçek endpoint mock'un {id,rol,unvan,adSoyad,telefon,email} alanlarının hepsini
    // taşıyor, üstüne fuarId/atamaBelgesiStatus/atamaBelgesiFileTypeId ekliyor — sayfa
    // kullanmadığı fazlalıkları yok sayar, çeviri gerekmiyor (bkz. CLAUDE.md kural 8).
    const response = await axios.get(`${BASE}/${firmaId}/saglik-guvenlik-yetkilileri`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching firma saglik guvenlik yetkilileri:', error);
    throw error;
  }
}

/**
 * Firma Kartı — firmanın KENDİ yetkili kaydını yönetmesi (Backlog #82).
 *
 * Denetmen tarafındaki `firmaService`in aynı isimli fonksiyonlarından iki farkla ayrılır:
 * (1) burada bir mock dalı var, çünkü bu sayfa demo görünümünde de çalışıyor;
 * (2) zarfı açıp doğrudan GÜNCEL LİSTEYİ döner — çağıran sayfanın `result/message` ile
 * işi yok, ekranı listeyle yeniden boyuyor.
 *
 * Yetki: backend `denyIfNotOwnFirma` uyguluyor, yani firma girişi yalnızca kendi firması
 * için geçer. HANGİ rolleri yönetebileceği ise frontend kararı —
 * `yetkiliGruplari.firmaYonetebilir` (#443: her rol, İSG dahil; yazma ayrıca FIRMA_PROFILI ≥ WRITE ister, #436).
 * POST/PUT/DELETE /firma/{firmaId}/yetkililer[/{id}]
 */
function _mockListe(firmaId) {
  if (!_mockSaglikGuvenlikYetkilileri[firmaId]) _mockSaglikGuvenlikYetkilileri[firmaId] = [];
  return _mockSaglikGuvenlikYetkilileri[firmaId];
}

// Mock'ta bağlı hesap alanları `_mockKullanicilar`dan türüyor (gerçekte `user`den canlı okunur).
function _mockKullaniciAlanlari(firmaId, kullaniciId) {
  const u = (_mockKullanicilar[firmaId] || []).find((k) => k.id === kullaniciId);
  return {
    kullaniciId: u ? u.id : null,
    kullaniciAdi: u ? u.email.split('@')[0] : null,
    kullaniciRol: u ? u.rol : null,
    kullaniciAktif: u ? u.durum === 'aktif' : null,
    kullaniciKayitTarihi: u?.kayitTarihi ? `${u.kayitTarihi}T00:00:00` : null,
    kullaniciSonGiris: u?.sonGiris ? `${u.sonGiris}T00:00:00` : null,
  };
}

export async function createFirmaYetkilisi(firmaId, payload = {}, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const liste = _mockListe(firmaId);
    const rol = _mockYetkiliRolleri.find((r) => r.kod === payload.rol);
    liste.push({
      id: Math.max(0, ...liste.map((k) => k.id)) + 1,
      rol: payload.rol,
      rolAd: rol?.ad ?? payload.rol,
      grupKod: rol?.grupKod ?? null,
      grupAd: rol?.grupAd ?? null,
      sira: rol?.sira ?? 0,
      unvan: payload.unvan || null,
      adSoyad: payload.adSoyad,
      telefon: payload.telefon || null,
      email: payload.email || null,
      fuarId: null,
      atamaBelgesiStatus: null,
      atamaBelgesiFileTypeId: rol?.atamaFileTypeId ?? null,
      departman: payload.departman || null,
      dogumTarihi: payload.dogumTarihi || null,
      linkedin: payload.linkedin || null,
      biyografi: payload.biyografi || null,
      fotografUrl: null,
      ..._mockKullaniciAlanlari(firmaId, payload.kullaniciId ?? null),
    });
    return liste.map((k) => ({ ...k }));
  }

  const response = await axios.post(`${BASE}/${firmaId}/yetkililer`, payload, { headers: getAuthHeader() });
  return response.data?.data ?? [];
}

export async function updateFirmaYetkilisi(firmaId, id, payload = {}, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const liste = _mockListe(firmaId);
    const row = liste.find((k) => k.id === id);
    if (row) {
      // Backend'deki PATCH semantiği: gönderilen anahtar değişir, boş gönderilen temizlenir.
      for (const key of ['adSoyad', 'unvan', 'telefon', 'email', 'departman', 'dogumTarihi', 'linkedin', 'biyografi']) {
        if (key in payload) row[key] = payload[key] || (key === 'adSoyad' ? row.adSoyad : null);
      }
      if ('kullaniciId' in payload) Object.assign(row, _mockKullaniciAlanlari(firmaId, payload.kullaniciId));
    }
    return liste.map((k) => ({ ...k }));
  }

  const response = await axios.put(`${BASE}/${firmaId}/yetkililer/${id}`, payload, { headers: getAuthHeader() });
  return response.data?.data ?? [];
}

export async function deleteFirmaYetkilisi(firmaId, id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const liste = _mockListe(firmaId);
    const i = liste.findIndex((k) => k.id === id);
    if (i >= 0) liste.splice(i, 1);
    return liste.map((k) => ({ ...k }));
  }

  const response = await axios.delete(`${BASE}/${firmaId}/yetkililer/${id}`, { headers: getAuthHeader() });
  return response.data?.data ?? [];
}

/**
 * Yetkili fotoğrafı (#105) — satır önceden var olmalı, yeniden yükleme öncekinin yerine geçer.
 * Mock'ta dosya yerel bir nesne URL'ine çevriliyor (sayfa kapanınca gider).
 * POST/DELETE /firma/{firmaId}/yetkililer/{id}/fotograf — güncel listeyi döner.
 */
export async function uploadYetkiliFotograf(firmaId, id, file, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const liste = _mockListe(firmaId);
    const row = liste.find((k) => k.id === id);
    if (row) row.fotografUrl = URL.createObjectURL(file);
    return liste.map((k) => ({ ...k }));
  }

  const form = new FormData();
  form.append('file', file);
  const response = await axios.post(`${BASE}/${firmaId}/yetkililer/${id}/fotograf`, form, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
  });
  return response.data?.data ?? [];
}

export async function deleteYetkiliFotograf(firmaId, id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const liste = _mockListe(firmaId);
    const row = liste.find((k) => k.id === id);
    if (row) row.fotografUrl = null;
    return liste.map((k) => ({ ...k }));
  }

  const response = await axios.delete(`${BASE}/${firmaId}/yetkililer/${id}/fotograf`, { headers: getAuthHeader() });
  return response.data?.data ?? [];
}

/**
 * Firma Kartı — Saka Grup ekibinin bu firma ile yaptığı görüşme kayıtları.
 * GET /firma/{firmaId}/gorusme-notlari
 */
export async function getFirmaGorusmeNotlari(firmaId, { forceLive = false, fuarId = TEST_FUAR_ID } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return (_mockGorusmeNotlari[firmaId] || []).map((n) => ({ ...n }));
  }

  try {
    // Ayrı bir /gorusme-notlari rotası yok — /{firmaId}/detay → talks aynı veriyi taşıyor
    // (bkz. rehber, yazma tarafı zaten POST /firma/talks). filterFuarId ile seçili fuara
    // daraltıyoruz; aksi halde firmanın TÜM fuarlardaki görüşmeleri gelirdi.
    const response = await axios.get(`${BASE}/${firmaId}/detay`, {
      params: { fuarId, filterFuarId: fuarId },
      headers: getAuthHeader(),
    });
    return (response.data.talks || []).map((t) => ({
      id: t.id,
      tarih: t.noteDate,
      saat: t.noteTime,
      gorusulen: `${t.companyUserName || ''} ${t.companyUserSurname || ''}`.trim() || '—',
      denetmen: `${t.adminName || ''} ${t.adminSurname || ''}`.trim() || '—',
      gorusmeTipi: t.gorusmeTipi,
      not: t.noteText,
    }));
  } catch (error) {
    console.error('Error fetching firma gorusme notlari:', error);
    throw error;
  }
}

/**
 * Eksik/yenilenecek bir fuar evrakını yükler — durum "Yüklendi"ye döner, admin
 * onayı ayrı bir akıştır (bu servis kapsamında değil).
 * POST /firma/{firmaId}/documents/{docKey} (multipart: file)
 */
export async function uploadFirmaDocument(firmaId, docKey, file) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const doc = (_mockDocuments[firmaId] || []).find((d) => d.key === docKey);
    if (doc) doc.status = 'yuklendi';
    return { success: true };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${BASE}/${firmaId}/documents/${docKey}`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading firma document:', error);
    throw error;
  }
}
